import {
   habitChangesResponseSchema,
   type HabitChangesResponse,
   type HabitPausePeriodRecord,
} from '@fasting-timer/shared'
import { safeParse } from 'valibot'

import { readErrorMessage } from './measurementsApi'

type HabitsApiDeps = {
   habitsEndpoint: string
   canUseRemoteSync: () => boolean
   getUserId: () => string
   resolveSyncUserId: () => Promise<string>
}

/** The editable fields `PUT /v1/habits/:id` accepts. */
export type HabitUpsertBody = {
   name: string
   kind: 'recurring' | 'one-off' | 'calendar'
   scheduleDays: number
   scheduleRule?: string
   dueDate: string | null
   paused: boolean
   pausePeriods: HabitPausePeriodRecord[]
   alsoCompletes: string[]
   tags: string[]
   updatedAt: string
}

/** Outcome of one write: done, superseded by a newer server edit, or the habit is gone. */
export type HabitWriteResult = 'ok' | 'stale' | 'missing'

/**
 * Thrown for a write the server refused as invalid (4xx other than 401/404);
 * retrying the same change cannot succeed, so the caller drops it.
 */
export class HabitWriteRejectedError extends Error {}

/**
 * Creates the per-habit `/v1/habits` client the Task Manager app also uses,
 * authenticated with the hashed user id as a bearer token.
 *
 * @param deps - Injected endpoint and user/sync accessors.
 * @returns The API methods.
 */
export function createHabitsApi(deps: HabitsApiDeps) {
   /**
    * Resolves the bearer token when a sync key is set and remote sync is usable.
    *
    * @returns The hashed id, or null when remote calls are not allowed.
    */
   async function resolveToken() {
      if (!deps.getUserId() || !deps.canUseRemoteSync()) {
         return null
      }
      return (await deps.resolveSyncUserId()) || null
   }

   /**
    * Sends one request.
    *
    * @param token - The bearer token.
    * @param method - The HTTP method.
    * @param path - The path below the habits endpoint (with query).
    * @param body - The JSON body, if any.
    * @returns The response.
    */
   function send(token: string, method: string, path: string, body?: unknown) {
      return fetch(`${deps.habitsEndpoint}${path}`, {
         method,
         headers: {
            Authorization: `Bearer ${token}`,
            ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
         },
         body: body === undefined ? undefined : JSON.stringify(body),
      })
   }

   /**
    * Lists habits and completions changed since a cursor.
    *
    * @param options - `since` ISO cursor from a previous response's `serverTime`.
    * @returns The parsed response, or null when remote sync is unavailable.
    */
   async function changes(options: { since?: string | null } = {}) {
      const token = await resolveToken()
      if (!token) {
         return null
      }
      const query = options.since ? `?since=${encodeURIComponent(options.since)}` : ''
      const response = await send(token, 'GET', `/changes${query}`)
      if (!response.ok) {
         throw new Error(
            await readErrorMessage(response, `Habits fetch failed (${response.status})`),
         )
      }
      const parsed = safeParse(habitChangesResponseSchema, await response.json())
      if (!parsed.success) {
         throw new Error('Habits response was malformed.')
      }
      return parsed.output satisfies HabitChangesResponse
   }

   /**
    * Sends one write and classifies the response.
    *
    * @param method - The HTTP method.
    * @param path - The path below the habits endpoint.
    * @param body - The JSON body, if any.
    * @returns The outcome, or null when remote sync is unavailable.
    */
   async function write(
      method: string,
      path: string,
      body?: unknown,
   ): Promise<HabitWriteResult | null> {
      const token = await resolveToken()
      if (!token) {
         return null
      }
      const response = await send(token, method, path, body)
      if (response.status === 404) {
         return 'missing'
      }
      if (!response.ok) {
         const message = await readErrorMessage(
            response,
            `Habit write failed (${response.status})`,
         )
         if (response.status >= 400 && response.status < 500 && response.status !== 401) {
            throw new HabitWriteRejectedError(message)
         }
         throw new Error(message)
      }
      const result = (await response.json()) as { stale?: unknown }
      return result.stale === true ? 'stale' : 'ok'
   }

   return {
      changes,
      upsert: (id: string, body: HabitUpsertBody) =>
         write('PUT', `/${encodeURIComponent(id)}`, body),
      complete: (id: string, completedAt: string) =>
         write('POST', `/${encodeURIComponent(id)}/complete`, { completedAt }),
      remove: (id: string) => write('DELETE', `/${encodeURIComponent(id)}`),
   }
}

export type HabitsApi = ReturnType<typeof createHabitsApi>
